import type { HoverIdleTargetCellBaseSnapshot } from "../types";
import { MHOV_ANIMATION_MS_FAST, MHOV_ANIMATION_MS_NORMAL } from "./mHovLogic";

export type MHovAnimationMode = "normal" | "fast" | "instant";

export interface MHovAnimationTiming {
  readonly startDelayMs: number;
  readonly normalDurationMs: number;
  readonly fastDurationMs: number;
  readonly mergePhaseRatio: number;
  readonly hideStaggerMs: number;
  readonly completeHoldMs: number;
  readonly targetScaleFallback: number;
}

export interface MHovAnimationOptions {
  readonly gridRoot: HTMLElement;
  readonly targetCells: readonly HoverIdleTargetCellBaseSnapshot[];
  readonly gridTier: number;
  readonly mode: MHovAnimationMode;
  readonly onComplete: () => void;
  readonly timing?: Partial<MHovAnimationTiming>;
}

export const DEFAULT_MHOV_ANIMATION_TIMING: MHovAnimationTiming = {
  startDelayMs: 0,
  normalDurationMs: MHOV_ANIMATION_MS_NORMAL,
  fastDurationMs: MHOV_ANIMATION_MS_FAST,
  mergePhaseRatio: 0.6,
  hideStaggerMs: 200,
  completeHoldMs: 100,
  targetScaleFallback: 1.35,
};

function clampMinNumber(value: unknown, fallback: number, min: number): number {
  return typeof value === "number" && Number.isFinite(value) && value >= min
    ? value
    : fallback;
}

function clampStrictlyPositive(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : fallback;
}

export function normalizeMHovAnimationTiming(
  override?: Partial<MHovAnimationTiming>,
): MHovAnimationTiming {
  const base = DEFAULT_MHOV_ANIMATION_TIMING;
  if (!override) return base;
  return {
    startDelayMs: clampMinNumber(override.startDelayMs, base.startDelayMs, 0),
    normalDurationMs: clampMinNumber(
      override.normalDurationMs,
      base.normalDurationMs,
      1,
    ),
    fastDurationMs: clampMinNumber(
      override.fastDurationMs,
      base.fastDurationMs,
      1,
    ),
    mergePhaseRatio:
      typeof override.mergePhaseRatio === "number" &&
      Number.isFinite(override.mergePhaseRatio) &&
      override.mergePhaseRatio > 0 &&
      override.mergePhaseRatio < 1
        ? override.mergePhaseRatio
        : base.mergePhaseRatio,
    hideStaggerMs: clampMinNumber(
      override.hideStaggerMs,
      base.hideStaggerMs,
      0,
    ),
    completeHoldMs: clampMinNumber(
      override.completeHoldMs,
      base.completeHoldMs,
      0,
    ),
    targetScaleFallback: clampStrictlyPositive(
      override.targetScaleFallback,
      base.targetScaleFallback,
    ),
  };
}

export interface MHovBoxSize {
  readonly width: number;
  readonly height: number;
}

export interface MHovGridSpacing {
  readonly paddingLeft: number;
  readonly paddingRight: number;
  readonly paddingTop: number;
  readonly paddingBottom: number;
}

export interface MHovCellAnimationPlan {
  readonly visibleUnlockedIndices: readonly number[];
  readonly disappearIndices: readonly number[];
}

export const MHOV_CELL_VISUAL_SELECTOR =
  ":scope > .hover-idle-target-cell-visual";

export function resolveMHovCellAnimationElement(
  cell: HTMLElement,
): HTMLElement {
  return cell.querySelector<HTMLElement>(MHOV_CELL_VISUAL_SELECTOR) ?? cell;
}

export function calculateMHovTier1TargetScale(options: {
  readonly cellRect: MHovBoxSize;
  readonly gridRect: MHovBoxSize;
  readonly gridSpacing: MHovGridSpacing;
}): number {
  const { cellRect, gridRect, gridSpacing } = options;
  const targetWidth =
    gridRect.width - gridSpacing.paddingLeft - gridSpacing.paddingRight;
  const targetHeight =
    gridRect.height - gridSpacing.paddingTop - gridSpacing.paddingBottom;
  const scaleX = targetWidth / cellRect.width;
  const scaleY = targetHeight / cellRect.height;
  const scale = Math.min(scaleX, scaleY);

  return Number.isFinite(scale) && scale > 0
    ? scale
    : DEFAULT_MHOV_ANIMATION_TIMING.targetScaleFallback;
}

export function calculateMHovFixedOverlayOffset(options: {
  readonly cellRect: MHovBoxSize;
  readonly scale: number;
}): { readonly x: number; readonly y: number } {
  const { cellRect, scale } = options;
  if (
    !Number.isFinite(cellRect.width) ||
    !Number.isFinite(cellRect.height) ||
    !Number.isFinite(scale) ||
    cellRect.width <= 0 ||
    cellRect.height <= 0 ||
    scale <= 1
  ) {
    return { x: 0, y: 0 };
  }

  return {
    x: -(cellRect.width * (scale - 1)) / 2,
    y: -(cellRect.height * (scale - 1)) / 2,
  };
}

export function createMHovCellAnimationPlan(options: {
  readonly targetCells: readonly HoverIdleTargetCellBaseSnapshot[];
  readonly gridTier: number;
}): MHovCellAnimationPlan {
  const { targetCells, gridTier } = options;
  const visibleUnlockedIndices: number[] = [];

  for (let idx = 0; idx < gridTier; idx += 1) {
    const snap = targetCells[idx];
    const isUnlocked = snap?.unlocked ?? idx === 0;
    if (isUnlocked) {
      visibleUnlockedIndices.push(idx);
    }
  }

  visibleUnlockedIndices.sort((a, b) => a - b);
  const disappearIndices = visibleUnlockedIndices
    .filter((idx) => idx !== 0)
    .sort((a, b) => b - a);

  return {
    visibleUnlockedIndices,
    disappearIndices,
  };
}

/**
 * Runs the MHov cell animation:
 * 1. Locked/hidden cells vanish immediately.
 * 2. Unlocked cells grow while moving toward the target grid center.
 * 3. Near the end, cells disappear highest index first without shrinking.
 * 4. Cell 1 remains last and settles back to the tier-1 target shape.
 * 5. onComplete fires when done; the caller applies the reset.
 *
 * Separation from reset logic: animation runs before model.executeMHov().
 */
export function runMHovAnimation(options: MHovAnimationOptions): () => void {
  const { gridRoot, targetCells, gridTier, mode, onComplete } = options;
  const timing = normalizeMHovAnimationTiming(options.timing);

  if (mode === "instant") {
    if (timing.startDelayMs > 0) {
      let active = true;
      const handle = setTimeout(() => {
        if (!active) return;
        onComplete();
      }, timing.startDelayMs);
      return () => {
        active = false;
        clearTimeout(handle);
      };
    }
    onComplete();
    return () => undefined;
  }

  const totalMs =
    mode === "fast" ? timing.fastDurationMs : timing.normalDurationMs;

  const cells = Array.from(
    gridRoot.querySelectorAll<HTMLElement>(".hover-idle-target-cell"),
  );
  const animationTargets = cells.map((cell) => ({
    cell,
    element: resolveMHovCellAnimationElement(cell),
    fixedOverlays: Array.from(
      cell.querySelectorAll<HTMLElement>(":scope > .hover-idle-mp-slot"),
    ),
  }));
  const plan = createMHovCellAnimationPlan({ targetCells, gridTier });
  const mergeDestinationRect = gridRoot.getBoundingClientRect();
  const mergeDestinationCenter = mergeDestinationRect
    ? {
        x: mergeDestinationRect.left + mergeDestinationRect.width / 2,
        y: mergeDestinationRect.top + mergeDestinationRect.height / 2,
      }
    : { x: 0, y: 0 };
  const gridStyle = getComputedStyle(gridRoot);
  const gridSpacing = {
    paddingLeft: readPixelValue(gridStyle.paddingLeft),
    paddingRight: readPixelValue(gridStyle.paddingRight),
    paddingTop: readPixelValue(gridStyle.paddingTop),
    paddingBottom: readPixelValue(gridStyle.paddingBottom),
  };

  // Apply z-index so higher-index cells render in front.
  for (const { cell } of animationTargets) {
    const idx = Number(cell.dataset.cellIndex ?? "0");
    cell.style.zIndex = String(idx + 1);
  }

  // Disable pointer events on the entire grid during animation.
  gridRoot.style.pointerEvents = "none";

  // Immediately hide locked / invisible cells.
  const visibleUnlockedIndices = new Set(plan.visibleUnlockedIndices);
  for (const { cell, element, fixedOverlays } of animationTargets) {
    const idx = Number(cell.dataset.cellIndex ?? "0");
    const isVisible = idx < gridTier;
    const isUnlocked = visibleUnlockedIndices.has(idx);
    if (!isVisible || !isUnlocked) {
      cell.style.transition = "none";
      cell.style.opacity = "0";
      element.style.transform = "scale(0)";
    } else {
      const rect = cell.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const mergeX = `${mergeDestinationCenter.x - centerX}px`;
      const mergeY = `${mergeDestinationCenter.y - centerY}px`;
      element.style.setProperty("--mhov-merge-x", mergeX);
      element.style.setProperty("--mhov-merge-y", mergeY);
      for (const overlay of fixedOverlays) {
        overlay.style.setProperty("--mhov-merge-x", mergeX);
        overlay.style.setProperty("--mhov-merge-y", mergeY);
      }
      const targetScale = calculateMHovTier1TargetScale({
        cellRect: rect,
        gridRect: mergeDestinationRect,
        gridSpacing,
      });
      const fixedOverlayOffset = calculateMHovFixedOverlayOffset({
        cellRect: rect,
        scale: targetScale,
      });
      element.style.setProperty("--mhov-merge-scale", String(targetScale));
      element.style.animation = "none";
      element.style.transformOrigin = "center";
      element.style.transition = `transform ${totalMs * timing.mergePhaseRatio}ms ease-out, opacity 300ms ease-in`;
      for (const overlay of fixedOverlays) {
        overlay.style.setProperty(
          "--mhov-overlay-x",
          `${fixedOverlayOffset.x}px`,
        );
        overlay.style.setProperty(
          "--mhov-overlay-y",
          `${fixedOverlayOffset.y}px`,
        );
        overlay.style.transformOrigin = "center";
        overlay.style.transition = `transform ${totalMs * timing.mergePhaseRatio}ms ease-out`;
      }
    }
  }
  const toHide = plan.disappearIndices;

  const timers: ReturnType<typeof setTimeout>[] = [];
  let animationFrame: number | null = null;
  let active = true;

  const cleanup = (): void => {
    active = false;
    if (animationFrame !== null) {
      cancelAnimationFrame(animationFrame);
      animationFrame = null;
    }
    for (const t of timers) {
      clearTimeout(t);
    }
    gridRoot.style.pointerEvents = "";
    for (const { cell, element, fixedOverlays } of animationTargets) {
      cell.style.zIndex = "";
      cell.style.transition = "";
      cell.style.opacity = "";
      cell.style.visibility = "";
      element.style.transition = "";
      element.style.transform = "";
      element.style.animation = "";
      element.style.transformOrigin = "";
      element.style.removeProperty("--mhov-merge-x");
      element.style.removeProperty("--mhov-merge-y");
      element.style.removeProperty("--mhov-merge-scale");
      for (const overlay of fixedOverlays) {
        overlay.style.transition = "";
        overlay.style.transform = "";
        overlay.style.transformOrigin = "";
        overlay.style.removeProperty("--mhov-merge-x");
        overlay.style.removeProperty("--mhov-merge-y");
        overlay.style.removeProperty("--mhov-overlay-x");
        overlay.style.removeProperty("--mhov-overlay-y");
      }
    }
  };

  // Phase 1: grow all visible unlocked cells.
  const triggerPhase1 = (): void => {
    if (!active) return;
    animationFrame = requestAnimationFrame(() => {
      animationFrame = null;
      if (!active) return;
      for (const idx of plan.visibleUnlockedIndices) {
        const target = animationTargets[idx];
        if (!target) continue;
        target.element.style.transform =
          "translate(var(--mhov-merge-x), var(--mhov-merge-y)) scale(var(--mhov-merge-scale))";
        for (const overlay of target.fixedOverlays) {
          overlay.style.transform =
            "translate(calc(var(--mhov-merge-x) + var(--mhov-overlay-x)), calc(var(--mhov-merge-y) + var(--mhov-overlay-y)))";
        }
      }
    });
  };
  if (timing.startDelayMs > 0) {
    timers.push(setTimeout(triggerPhase1, timing.startDelayMs));
  } else {
    triggerPhase1();
  }

  // Phase 2: after grow phase, hide cells from highest index downward.
  const hideStartMs = timing.startDelayMs + totalMs * timing.mergePhaseRatio;
  for (let i = 0; i < toHide.length; i += 1) {
    const idx = toHide[i];
    const delay = hideStartMs + i * timing.hideStaggerMs;
    timers.push(
      setTimeout(() => {
        if (!active) return;
        const el = cells[idx];
        if (!el) return;
        el.style.transition = "none";
        el.style.opacity = "0";
        el.style.visibility = "hidden";
      }, delay),
    );
  }

  // Complete while the merge posture is still held. The caller can reset/render
  // before the next frame clears inline animation state.
  const completeDelay =
    hideStartMs + toHide.length * timing.hideStaggerMs + timing.completeHoldMs;
  timers.push(
    setTimeout(() => {
      if (!active) return;
      onComplete();
      animationFrame = requestAnimationFrame(() => {
        animationFrame = null;
        if (!active) return;
        cleanup();
      });
    }, completeDelay),
  );

  return cleanup;
}

function readPixelValue(value: string): number {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}
